/**
 * Modality Subscription Manager Component
 *
 * Allows admins to manage which modalities a radiologist is subscribed to.
 * Used in user edit modal/page for radiologist users.
 */

import { useState, useEffect } from 'react'
import { Loader2, Check, AlertCircle, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/presentation/components/ui/Badge'
import {
  useActiveModalities,
} from '@/application/modalities'
import {
  useUserSubscriptions,
  useUpdateUserSubscriptions,
} from '@/application/modality-subscriptions'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'

// =============================================================================
// Types
// =============================================================================

interface ModalitySubscriptionManagerProps {
  /** User ID to manage subscriptions for */
  userId: string
  /** User's role - only show for radiologists */
  userRole: string
  /** Callback when subscriptions change */
  onSuccess?: () => void
  /** Read-only mode */
  readOnly?: boolean
  /** Additional CSS classes */
  className?: string
}

// =============================================================================
// Component
// =============================================================================

export function ModalitySubscriptionManager({
  userId,
  userRole,
  onSuccess,
  readOnly = false,
  className,
}: ModalitySubscriptionManagerProps) {
  // State
  const [selectedModalityIds, setSelectedModalityIds] = useState<Set<string>>(new Set())

  // Queries
  const { data: modalitiesData, isLoading: isLoadingModalities } = useActiveModalities()
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } = useUserSubscriptions(userId)

  // Mutations
  const updateMutation = useUpdateUserSubscriptions()

  // Initialize selected modalities from subscriptions
  useEffect(() => {
    if (subscriptionsData?.subscriptions) {
      const subscribedIds = new Set(
        subscriptionsData.subscriptions.map((s) => s.modalityId)
      )
      setSelectedModalityIds(subscribedIds)
    }
  }, [subscriptionsData])

  const modalities = modalitiesData?.modalities || []
  const isLoading = isLoadingModalities || isLoadingSubscriptions

  // Check if this is a radiologist
  const isRadiologist = userRole === 'RADIOLOGIST'

  // Auto-save function
  const saveSubscriptions = async (newIds: Set<string>) => {
    try {
      await updateMutation.mutateAsync({
        userId,
        modalityIds: Array.from(newIds),
      })
      onSuccess?.()
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar suscripciones'))
    }
  }

  // Handlers
  const handleToggle = (modalityId: string) => {
    if (readOnly) return

    const newIds = new Set(selectedModalityIds)
    if (newIds.has(modalityId)) {
      newIds.delete(modalityId)
    } else {
      newIds.add(modalityId)
    }
    setSelectedModalityIds(newIds)
    // Auto-save on toggle
    saveSubscriptions(newIds)
  }

  const handleSelectAll = () => {
    if (readOnly) return

    const newIds = new Set(modalities.map((m) => m.id))
    setSelectedModalityIds(newIds)
    saveSubscriptions(newIds)
  }

  const handleSelectNone = () => {
    if (readOnly) return

    const newIds = new Set<string>()
    setSelectedModalityIds(newIds)
    saveSubscriptions(newIds)
  }

  // Don't render if not a radiologist
  if (!isRadiologist) {
    return null
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-[var(--foreground-muted)]" />
          <h3 className="font-medium text-[var(--foreground)]">
            Modalidades Asignadas
          </h3>
        </div>
        {!readOnly && modalities.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-[var(--primary)] hover:underline"
            >
              Todas
            </button>
            <span className="text-[var(--foreground-muted)]">|</span>
            <button
              type="button"
              onClick={handleSelectNone}
              className="text-[var(--primary)] hover:underline"
            >
              Ninguna
            </button>
          </div>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-[var(--foreground-muted)]">
        Seleccione las modalidades que este radiólogo puede atender.
        Solo recibirá estudios de las modalidades seleccionadas.
      </p>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
        </div>
      ) : modalities.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <AlertCircle className="h-8 w-8 text-[var(--foreground-muted)] mb-2" />
          <p className="text-sm text-[var(--foreground-muted)]">
            No hay modalidades activas
          </p>
        </div>
      ) : (
        <>
          {/* Modality Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {modalities.map((modality) => {
              const isSelected = selectedModalityIds.has(modality.id)

              return (
                <button
                  key={modality.id}
                  type="button"
                  onClick={() => handleToggle(modality.id)}
                  disabled={readOnly}
                  className={cn(
                    'flex items-center gap-2 p-3 rounded-lg border transition-colors text-left',
                    isSelected
                      ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                      : 'border-[var(--border)] hover:border-[var(--primary)]/50',
                    readOnly && 'cursor-default',
                    !readOnly && 'hover:bg-[var(--background-subtle)]'
                  )}
                >
                  <div
                    className={cn(
                      'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                      isSelected
                        ? 'border-[var(--primary)] bg-[var(--primary)]'
                        : 'border-[var(--border)]'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {modality.code}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium text-[var(--foreground)] truncate mt-0.5">
                      {modality.name}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Summary */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[var(--border)]">
            <span className="text-sm text-[var(--foreground-muted)]">
              {selectedModalityIds.size} de {modalities.length} modalidades seleccionadas
            </span>

            {updateMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-[var(--foreground-muted)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Guardando...</span>
              </div>
            )}
          </div>

          {/* Error message */}
          {updateMutation.isError && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">
                Error al guardar las suscripciones. Intente nuevamente.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// =============================================================================
// Compact Version for inline display
// =============================================================================

interface ModalitySubscriptionBadgesProps {
  userId: string
  className?: string
}

export function ModalitySubscriptionBadges({
  userId,
  className,
}: ModalitySubscriptionBadgesProps) {
  const { data, isLoading } = useUserSubscriptions(userId)

  if (isLoading) {
    return <Loader2 className="h-4 w-4 animate-spin" />
  }

  const subscriptions = data?.subscriptions || []

  if (subscriptions.length === 0) {
    return (
      <span className="text-xs text-[var(--foreground-muted)] italic">
        Sin modalidades
      </span>
    )
  }

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {subscriptions.slice(0, 4).map((sub) => (
        <Badge key={sub.id} variant="outline" className="text-xs font-mono">
          {sub.modalityCode}
        </Badge>
      ))}
      {subscriptions.length > 4 && (
        <Badge variant="secondary" className="text-xs">
          +{subscriptions.length - 4}
        </Badge>
      )}
    </div>
  )
}
